import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map, Observable, shareReplay } from 'rxjs';
import { SocialDomainService } from '../../_services/social-domain.service';
import { WorkProcess } from '../../_models/social-domain/workprocess.model';
import { Client } from '../../_models/social-domain/client.model';
import { WorkProcessWithClient } from '../../_models/social-domain/workprocess-with-client.model';
import { InlineSnackbar } from '../inline-snackbar/inline-snackbar';
import { ClientSearch } from '../client-search/client-search';
import { DocxPreview } from '../document-editor/docx-preview';
import { SnackbarType } from '../../_enums/snackbar-type.enum';
import { WorkflowService } from '../../_services/workflow.service';
import { TaskService } from '../../_services/task.service';

@Component({
  selector: 'app-document-summary',
  imports: [
    TranslatePipe,
    MatButton,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    InlineSnackbar,
    ClientSearch,
  ],
  templateUrl: './document-summary.html',
  styleUrl: './document-summary.scss',
})
export class DocumentSummary implements OnInit {
  @Input() preselectedWorkProcess: WorkProcessWithClient | null = null;
  @Output() summaryGenerated = new EventEmitter<void>();

  private readonly socialDomainService = inject(SocialDomainService);
  private readonly workflowService = inject(WorkflowService);
  private readonly taskService = inject(TaskService);
  private readonly docxPreview = inject(DocxPreview);
  private readonly translateService = inject(TranslateService);

  public workProcesses: WorkProcess[] = [];
  public selectedClient: Client | null = null;
  public selectedWorkProcess: number | null = null;
  public areWorkProcessesLoading = false;
  public workProcessesError = false;
  public workProcessesNotFound = false;
  public downloadError: string | null = null;
  public downloadErrorType: 'danger' | 'warning' | null = null;
  public isGenerating = false;
  public skippedDocuments: string[] = [];
  public summaryFile: File | null = null;
  private pendingSummary$: Observable<File> | null = null;

  public readonly SnackbarType = SnackbarType;

  public ngOnInit(): void {
    this.applyPreselection();
  }

  public onClientSelected(client: Client | null): void {
    this.selectedClient = client;
    this.workflowService.setStepInProgress(true);
    this.selectedWorkProcess = null;
    this.workProcesses = [];
    this.workProcessesError = false;
    this.workProcessesNotFound = false;
    this.downloadError = null;
    this.downloadErrorType = null;
    this.summaryFile = null;
    if (client) {
      this.areWorkProcessesLoading = true;
      this.socialDomainService.getWorkProcessesByClient(client.nummer).subscribe({
        next: (workProcesses) => {
          this.workProcesses = workProcesses;
          this.areWorkProcessesLoading = false;
          if (!workProcesses || workProcesses.length === 0) {
            this.workProcessesNotFound = true;
          }
        },
        error: () => {
          this.areWorkProcessesLoading = false;
          this.workProcessesError = true;
        },
      });
    }
  }

  public generateSummary(): void {
    if (!this.selectedClient) return;

    this.isGenerating = true;
    this.downloadError = null;
    this.downloadErrorType = null;
    this.skippedDocuments = [];
    this.summaryFile = null;

    // Workproces picked: summarize workproces
    // No workproces & only client picked: summarize every workproces from said client
    const request$ =
      this.selectedWorkProcess != null
        ? this.taskService.summarizeWerkprocesFiles(this.selectedWorkProcess)
        : this.taskService.summarizeClientFiles(this.selectedClient.nummer);

    const fileSuffix =
      this.selectedWorkProcess != null
        ? `${this.selectedWorkProcess}`
        : `client-${this.selectedClient.nummer}`;

    const summary$ = request$.pipe(
      map((response) => {
        const skippedHeader = response.headers.get('X-Skipped-Documents');
        this.skippedDocuments = skippedHeader ? skippedHeader.split(',') : [];

        return new File([response.body!], `samenvatting-${fileSuffix}.docx`, {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
      }),
      shareReplay(1),
    );

    this.pendingSummary$ = summary$;

    summary$.subscribe({
      next: (file) => {
        this.pendingSummary$ = null;
        this.summaryFile = file;
        this.isGenerating = false;
        this.summaryGenerated.emit();
      },
      error: (error) => {
        this.pendingSummary$ = null;
        this.isGenerating = false;
        if (error?.status === 422) {
          this.downloadError = 'documentSummary.noUsableDocuments';
          this.downloadErrorType = 'warning';
        } else if (error?.status === 404) {
          this.downloadError = 'documentSummary.noDocuments';
          this.downloadErrorType = 'warning';
        } else {
          this.downloadError = 'documentSummary.generateError';
          this.downloadErrorType = 'danger';
        }
      },
    });

    this.openPreview(summary$);
  }

  public previewSummary(): void {
    const file = this.pendingSummary$ ?? this.summaryFile;
    if (!file) {
      return;
    }

    this.openPreview(file);
  }

  public formatWorkProcessLabel(workProcess: WorkProcess): string {
    return `${workProcess.werkprocesNummer}`;
  }

  public onWorkProcessChange(): void {
    this.workflowService.setStepInProgress(true);
    this.downloadError = null;
    this.downloadErrorType = null;
    this.summaryFile = null;
  }

  private openPreview(file: File | Observable<File>): void {
    this.docxPreview.open({
      file,
      title: this.translateService.instant('stepper.caseSummary'),
      edited: (edited) => (this.summaryFile = edited),
    });
  }

  private applyPreselection(): void {
    if (!this.preselectedWorkProcess?.client) return;

    this.selectedClient = this.preselectedWorkProcess.client;
    this.areWorkProcessesLoading = true;
    this.socialDomainService.getWorkProcessesByClient(this.selectedClient.nummer).subscribe({
      next: (workProcesses) => {
        this.workProcesses = workProcesses;
        this.areWorkProcessesLoading = false;
        if (!workProcesses || workProcesses.length === 0) {
          this.workProcessesNotFound = true;
        } else {
          this.selectedWorkProcess = this.preselectedWorkProcess!.werkprocesNummer;
        }
      },
      error: () => {
        this.areWorkProcessesLoading = false;
        this.workProcessesError = true;
      },
    });
  }
}
